import {
  IHouseStepUniqueness,
  IWizardHouseState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable,
  TWizardStepComponentEditableView,
  WIZARD_HOUSE_STEPS_PATHS
} from "../../../interfaces";
import { Form, Input } from "antd";
import { StepViewOnlyValue } from "../../../Student/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createCheckIsReadonlyStepField,
  getChangedFields,
  getStepErrorsFromResponse
} from "../../../utils";

import { HouseAPI } from "../../../../../API/houseApi";
import { StudentMatchCharacteristicsValues } from "../../../../../interfaces/interfaces";
import { MATCH_CHARACTERISTICS_ICONS } from "../../../Student/Steps/WizardStepMatchExpectations";
import { WizardButtonSelect } from "../../../components";

const statePath = WIZARD_HOUSE_STEPS_PATHS.Uniqueness;
export const WizardStepUniqueness: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IHouseStepUniqueness>>(
      state[statePath] ?? {}
    );
    const [errors, setErrors] = useState<TStepErrors<typeof values>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return HouseAPI.wizardSaveUniqueness(
          state.house_id!,
          state.wizard_type!,
          values
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return HouseAPI.wizardSaveUniquenessPartially(
          state.house_id!,
          state.wizard_type!,
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepUniquenessView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

export const WizardStepUniquenessView: TWizardStepComponentEditableView<IHouseStepUniqueness> =
  React.memo(
    ({ errors, values, setValues, viewonly, readonly, enabledFields }) => {
      const { t } = useTranslation("house");

      const inputSize = "large";

      const accommodationOptions = StudentMatchCharacteristicsValues.map(
        (c) => ({
          value: c,
          label: t(`wizard.steps.Uniqueness.form.accommodation.options.${c}`),
          icon: MATCH_CHARACTERISTICS_ICONS[c]
        })
      );
      const isReadonly = createCheckIsReadonlyStepField(
        readonly,
        enabledFields
      );
      return (
        <Form layout="vertical">
          <Form.Item
            label={t(`wizard.steps.Uniqueness.form.accommodation.title`)}
            validateStatus={errors?.accommodation ? "error" : ""}
            help={errors?.accommodation ? t("wizard.errors.generic") : ""}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {accommodationOptions
                  .filter((c) => values.accommodation?.includes(c.value))
                  ?.map((o) => o.label)
                  .join(", ") ?? ""}
              </StepViewOnlyValue>
            ) : (
              <WizardButtonSelect
                disabled={isReadonly("accommodation")}
                value={values.accommodation ?? undefined}
                options={accommodationOptions}
                onChange={(newValue) =>
                  setValues({
                    ...values,
                    accommodation: newValue
                  })
                }
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(`wizard.steps.Uniqueness.form.place_description.title`)}
            validateStatus={errors?.place_description ? "error" : ""}
            help={errors?.place_description ? t("wizard.errors.generic") : ""}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {values.place_description ?? ""}
              </StepViewOnlyValue>
            ) : (
              <Input.TextArea
                size={inputSize}
                disabled={isReadonly("place_description")}
                value={values.place_description ?? undefined}
                placeholder={t(
                  `wizard.steps.Uniqueness.form.place_description.title`
                )}
                rows={3}
                onChange={(e) => {
                  setValues({
                    ...values,
                    place_description: e.target.value
                  });
                }}
              />
            )}
          </Form.Item>
        </Form>
      );
    }
  );
